import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { StockMovementSchema, StockMovementCreateInputSchema, StockMovementUpdateInputSchema, type StockMovement, type StockMovementCreateInput, type StockMovementUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `stockMovement` (mouvement de stock, org-wide).
 *
 * Réplique le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`StockMovementCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { StockMovementCreateInput };

/** Nom de la collection Firestore de la famille stockMovement. */
export const STOCK_MOVEMENTS_COLLECTION = 'stockMovements';

/**
 * Repository typé pour la famille `stockMovement`.
 * Toute lecture est validée par `StockMovementSchema`, toute écriture par
 * `StockMovementCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface StockMovementRepository {
  create(input: StockMovementCreateInput): Promise<StockMovement>;
  update(id: string, patch: StockMovementUpdateInput): Promise<StockMovement>;
  list(options?: ListOptions): Promise<StockMovement[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<StockMovement | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<StockMovement>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<StockMovement>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<StockMovement>;
}

const stockMovementConverter = createConverter<StockMovement>((raw: DocumentData) => StockMovementSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createStockMovementRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): StockMovementRepository {
  const col = collection(db, STOCK_MOVEMENTS_COLLECTION).withConverter(stockMovementConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<StockMovement>(db, col, STOCK_MOVEMENTS_COLLECTION, getActorId);

  return {
    async create(input: StockMovementCreateInput): Promise<StockMovement> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = StockMovementCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.fromPlaceId !== undefined) optional['fromPlaceId'] = fields.fromPlaceId;
      if (fields.toPlaceId !== undefined) optional['toPlaceId'] = fields.toPlaceId;
      if (fields.reason !== undefined) optional['reason'] = fields.reason;
      if (fields.lotId !== undefined) optional['lotId'] = fields.lotId;
      if (fields.movedAt !== undefined) optional['movedAt'] = fields.movedAt;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.shipmentId !== undefined) optional['shipmentId'] = fields.shipmentId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        productId: fields.productId,
        quantity: fields.quantity,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as StockMovement);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return StockMovementSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: StockMovementUpdateInput): Promise<StockMovement> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = StockMovementUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, STOCK_MOVEMENTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`stockMovement ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<StockMovement[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<StockMovement | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
